import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Save, Loader2, Building2, MapPin, CreditCard, Phone, Package, KeyRound,
  Search, Plus, Trash2, CheckCircle2, AlertCircle, Network, FileText,
} from 'lucide-react'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Button } from '@/components/ui/Button'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { CommissionInfoPanel } from '@/components/commercial/CommissionInfoPanel'
import { CompanyDocumentsSection } from './CompanyDocumentsSection'
import { useZodForm } from '@/hooks/useZodForm'
import { useAuthority } from '@/hooks/useAuthority'
import { applyApiErrorsToForm } from '@/lib/api-errors'
import { onValidationFail } from '@/lib/form-helpers'
import { isValidCpf, isValidCnpj, slugifyUsername, normalizeForMatch } from '@/lib/validators'
import {
  CompaniesService, type Company, type CompanyContact, type CompanyContactType,
  type ManagerGroup,
} from '@/services/modules/CompaniesService'
import { PlansService, type Plan } from '@/services/modules/PlansService'
import { ModalitiesService, type Modality } from '@/services/modules/ModalitiesService'
import { LookupService, type State, type City } from '@/services/modules/LookupService'
import { SalesLinkService } from '@/services/modules/SalesLinkService'
import { toast } from '@/components/ui/Toast'

/**
 * Form de Empresa em PÁGINA DEDICADA com 6 seções (FormSidemenu):
 *   1) Dados        — nome, razão social, tipo, CPF/CNPJ válido + único, gestor agrupado
 *   2) Localização  — CEP com lupa dentro do input, grid 3-col
 *   3) Faturamento  — dia de vencimento + método (só Boleto)
 *   4) Contatos     — lista múltipla com ConfirmDialog ao remover
 *   5) Planos       — matriz por modalidade (1 plano por modalidade, opcionais)
 *   6) Login        — username slugificado + check assíncrono, senha com olhinho
 */

// ============================================================================
// Helpers de máscara
// ============================================================================
const onlyDigits = (v: string) => v.replace(/\D+/g, '')

function maskCpf(v: string): string {
  const d = onlyDigits(v).slice(0, 11)
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d{1,2}).*/, '$1.$2.$3-$4')
}
function maskCnpj(v: string): string {
  const d = onlyDigits(v).slice(0, 14)
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2}).*/, '$1.$2.$3/$4-$5')
}
function maskCep(v: string): string {
  return onlyDigits(v).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2')
}
function maskPhone(v: string): string {
  const d = onlyDigits(v).slice(0, 11)
  if (d.length <= 10) {
    return d
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/^\((\d{2})\) (\d{4})(\d)/, '($1) $2-$3')
  }
  return d
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/^\((\d{2})\) (\d{5})(\d)/, '($1) $2-$3')
}

// ============================================================================
// Schema
// ============================================================================
// Aceita CEP com ou sem hífen (o input usa máscara "00000-000"). A validação
// real é feita por contagem de dígitos no superRefine abaixo.
const CEP_RE   = /^\d{5}-?\d{3}$/
const PHONE_RE = /^\d{10,11}$/

const contactSchema = z.object({
  type:  z.enum(['EMAIL', 'TELEFONE', 'CELULAR', 'WHATSAPP']),
  // label é opcional (apelido do contato; mantido nullable pra casar com
  // CompanyContact da API).
  label: z.string().nullable().optional().transform(v => v ?? null),
  value: z.string(),
}).superRefine((c, ctx) => {
  // Valor vazio só é erro DEPOIS de tentar submeter (RHF default já marca campo
  // como dirty). Quando vazio, mensagem amigável; quando preenchido, valida formato.
  const trimmed = c.value.trim()
  if (trimmed === '') {
    ctx.addIssue({ code: 'custom', path: ['value'], message: 'Informe o contato.' })
    return
  }
  if (c.type === 'EMAIL') {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      ctx.addIssue({ code: 'custom', path: ['value'], message: 'E-mail inválido.' })
    }
  } else {
    if (!PHONE_RE.test(onlyDigits(trimmed))) {
      ctx.addIssue({ code: 'custom', path: ['value'], message: 'Telefone deve ter 10 ou 11 dígitos.' })
    }
  }
})

const schema = z.object({
  // Dados
  name:          z.string().trim().min(1, 'Informe o nome da empresa.').max(150),
  corporateName: z.string().trim().min(1, 'Informe a razão social.').max(200),
  type:          z.enum(['PESSOA FÍSICA', 'PESSOA JURÍDICA']),
  cpfCnpj:       z.string().trim().min(1, 'Informe o CPF/CNPJ.'),
  managerId:     z.string().min(1, 'Selecione o gestor responsável.'),
  status:        z.enum(['ATIVO', 'INATIVO']),

  // Localização
  postalCode: z.string().trim().regex(CEP_RE, 'CEP deve ter 8 dígitos.').or(z.literal('')).nullable(),
  address:    z.string().trim().max(200).or(z.literal('')).nullable(),
  number:     z.string().trim().max(20).or(z.literal('')).nullable(),
  complement: z.string().trim().max(100).or(z.literal('')).nullable(),
  district:   z.string().trim().max(100).or(z.literal('')).nullable(),
  stateId:    z.string().nullable(),
  cityId:     z.string().nullable(),

  // Faturamento
  dueDay:        z.number({ message: 'Selecione o dia de vencimento.' }).int(),
  paymentMethod: z.enum(['BOLETO'], { message: 'Selecione o método de pagamento.' }),

  // Observações
  observations: z.string().nullable(),

  // Contatos
  contacts: z.array(contactSchema),

  // Planos
  planSelections: z.record(z.string(), z.string().nullable()),

  // Login
  username: z.string().trim().min(1, 'Informe o usuário.').max(80),
  email:    z.string().trim().email('E-mail inválido.').or(z.literal('')).nullable(),
  password: z.string().trim(),
}).superRefine((val, ctx) => {
  // Valida CPF (PF) ou CNPJ (PJ) com algoritmo dos dígitos verificadores
  const digits = onlyDigits(val.cpfCnpj)
  if (val.type === 'PESSOA FÍSICA') {
    if (digits.length !== 11) {
      ctx.addIssue({ code: 'custom', path: ['cpfCnpj'], message: 'CPF deve ter 11 dígitos.' })
    } else if (!isValidCpf(digits)) {
      ctx.addIssue({ code: 'custom', path: ['cpfCnpj'], message: 'CPF inválido — confira os dígitos.' })
    }
  } else {
    if (digits.length !== 14) {
      ctx.addIssue({ code: 'custom', path: ['cpfCnpj'], message: 'CNPJ deve ter 14 dígitos.' })
    } else if (!isValidCnpj(digits)) {
      ctx.addIssue({ code: 'custom', path: ['cpfCnpj'], message: 'CNPJ inválido — confira os dígitos.' })
    }
  }
})
type FormValues = z.infer<typeof schema>

// ============================================================================
// Page
// ============================================================================
export function CompanyFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id
  const { can } = useAuthority()
  const canSave = can(isEdit ? 'commercial.companies.edit' : 'commercial.companies.create')
  const [loading, setLoading] = useState(isEdit)
  const [activeSection, setActiveSection] = useState('basic')

  const [managerGroups, setManagerGroups] = useState<ManagerGroup[]>([])
  const [plans, setPlans]                 = useState<Plan[]>([])
  const [modalities, setModalities]       = useState<Modality[]>([])
  const [states, setStates]               = useState<State[]>([])
  const [cities, setCities]               = useState<City[]>([])
  const [cepLoading, setCepLoading]       = useState(false)
  const [cnpjLoading, setCnpjLoading]     = useState(false)

  // Senha obrigatória no cadastro, opcional na edição
  const dynamicSchema = useMemo(
    () => schema.superRefine((val, ctx) => {
      if (!isEdit && val.password.length < 8) {
        ctx.addIssue({ code: 'custom', path: ['password'], message: 'Senha deve ter no mínimo 8 caracteres.' })
      } else if (isEdit && val.password.length > 0 && val.password.length < 8) {
        ctx.addIssue({ code: 'custom', path: ['password'], message: 'Senha deve ter no mínimo 8 caracteres.' })
      }
    }),
    [isEdit],
  )

  const form = useZodForm(dynamicSchema, {
    defaultValues: {
      name: '', corporateName: '', type: 'PESSOA JURÍDICA', cpfCnpj: '', managerId: '', status: 'ATIVO',
      postalCode: null, address: null, number: null, complement: null, district: null, stateId: null, cityId: null,
      dueDay: 5, paymentMethod: 'BOLETO', observations: null,
      contacts: [],
      planSelections: {},
      username: '', email: '', password: '',
    },
  })
  const personType = form.watch('type')
  const stateId    = form.watch('stateId')
  const username   = form.watch('username')
  const cpfCnpj    = form.watch('cpfCnpj')
  // Valores pendentes de confirmação quando o documento já existe (duplicado permitido).
  const [dupConfirm, setDupConfirm] = useState<FormValues | null>(null)

  // ===== Carrega catálogos =====
  useEffect(() => {
    CompaniesService.managerOptions().then(setManagerGroups).catch(() => setManagerGroups([]))
    PlansService.list({ size: 100, status: 'ATIVO' }).then(r => setPlans(r.data)).catch(() => setPlans([]))
    ModalitiesService.list({ size: 100, status: 'ATIVO' }).then(r => setModalities(r.data)).catch(() => setModalities([]))
    LookupService.states().then(setStates).catch(() => setStates([]))
  }, [])

  useEffect(() => {
    if (!stateId) { setCities([]); return }
    // Pede só as cidades desse estado (filtro no backend) — agora temos
    // ~5570 cidades, não dá pra puxar tudo.
    LookupService.cities(stateId)
      .then(setCities)
      .catch(() => setCities([]))
  }, [stateId])

  // ===== Carrega company em edição =====
  useEffect(() => {
    if (!isEdit || !id) return
    setLoading(true)
    CompaniesService.show(id)
      .then((c: Company) => {
        const cityRel = c.address?.city
        const planSelections: Record<string, string | null> = {}
        for (const p of c.plans ?? []) {
          if (p.planTypeRelationshipId) planSelections[p.planTypeRelationshipId] = p.id
        }
        form.reset({
          name:          c.name,
          corporateName: c.corporateName ?? '',
          type:          (c.type as FormValues['type']) ?? 'PESSOA JURÍDICA',
          cpfCnpj:       c.cpfCnpj ? (c.type === 'PESSOA FÍSICA' ? maskCpf(c.cpfCnpj) : maskCnpj(c.cpfCnpj)) : '',
          managerId:     c.manager?.id ?? '',
          status:        c.status,
          postalCode:    c.address?.postalCode ? maskCep(c.address.postalCode) : null,
          address:       c.address?.address ?? null,
          number:        c.address?.number ?? null,
          complement:    c.address?.complement ?? null,
          district:      c.address?.district ?? null,
          stateId:       cityRel?.state?.id ?? cityRel?.stateId ?? null,
          cityId:        cityRel?.id ?? null,
          dueDay:        c.dueDay ?? 5,
          paymentMethod: 'BOLETO',
          observations:  c.observations ?? null,
          contacts: (c.contacts ?? []).map(ct => ({
            type:  ct.type,
            value: ct.type === 'EMAIL' ? ct.value : maskPhone(ct.value),
          })),
          planSelections,
          username: c.username ?? '',
          email: c.email ?? '',
          password: '',
        })
      })
      .catch(() => toast.error({ title: 'Não encontrada', description: 'Empresa não localizada.' }))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  // ===== Autofill CEP (disparado APENAS pelo clique na lupa) =====
  // O spinner do botão da lupa (cepLoading) já comunica que a busca está em
  // andamento — não usamos toast loading pra evitar duplicação. Apenas erros
  // de fato disparam toast (ex.: CEP inválido / não encontrado / falha de rede).
  async function lookupCep() {
    const cleaned = onlyDigits(form.getValues('postalCode') ?? '')
    if (cleaned.length !== 8) {
      toast.error({ title: 'CEP inválido', description: 'O CEP precisa ter 8 dígitos.' })
      return
    }
    setCepLoading(true)
    try {
      const data = await SalesLinkService.lookupCep(cleaned)
      if (!data) {
        toast.error({ title: 'CEP não encontrado', description: 'Verifique o CEP digitado.' })
        return
      }
      form.setValue('address',  data.address,  { shouldValidate: false })
      form.setValue('district', data.district, { shouldValidate: false })
      const st = states.find(s => s.abbreviation === data.stateAbbr)
      if (!st) {
        toast.warning({ title: 'Estado não localizado', description: 'Preencha o endereço manualmente.' })
        return
      }

      form.setValue('stateId', st.id, { shouldValidate: false })

      // Carrega cidades desse estado. Se a cidade do CEP ainda não existir
      // (caso raro após o seed do IBGE), chama findOrCreateCity para criar.
      let cs = await LookupService.cities(st.id)
      // Match ignora acento+caixa para casar dados ViaCEP/BrasilAPI com a base.
      const targetCityNorm = normalizeForMatch(data.cityName)
      let ci = cs.find(c => normalizeForMatch(c.city) === targetCityNorm)
      if (!ci) {
        try {
          const created = await LookupService.findOrCreateCity(data.cityName, st.abbreviation)
          ci = created
          cs = [...cs, created].sort((a, b) => a.city.localeCompare(b.city, 'pt-BR'))
        } catch {
          toast.error({ title: 'Cidade não encontrada', description: 'Selecione manualmente.' })
        }
      }
      setCities(cs)
      if (ci) form.setValue('cityId', ci.id, { shouldValidate: false })
    } catch {
      toast.error({ title: 'Falha ao buscar CEP', description: 'Verifique sua conexão e tente novamente.' })
    } finally {
      setCepLoading(false)
    }
  }

  // ===== Submit =====
  // Documento (CPF/CNPJ) pode repetir; se já existir, confirma em modal antes de salvar.
  async function onSubmit(values: FormValues) {
    if (!canSave) return
    try {
      const available = await CompaniesService.checkCpfCnpj(onlyDigits(values.cpfCnpj), isEdit ? id : undefined)
      if (!available) { setDupConfirm(values); return }
    } catch { /* falha na checagem não bloqueia — segue o save */ }
    await doSave(values)
  }

  async function doSave(values: FormValues) {
    try {
      const planIds = Object.values(values.planSelections).filter((v): v is string => !!v)

      const payload: Record<string, unknown> = {
        name:           values.name,
        corporate_name: values.corporateName || null,
        cpf_cnpj:       onlyDigits(values.cpfCnpj),
        type:           values.type,
        manager_id:     values.managerId,
        status:         values.status,
        postal_code:    values.postalCode ? onlyDigits(values.postalCode) : null,
        address:        values.address || null,
        number:         values.number || null,
        complement:     values.complement || null,
        district:       values.district || null,
        city_id:        values.cityId || null,
        due_day:        values.dueDay,
        payment_method: values.paymentMethod,
        observations:   values.observations || null,
        username:       values.username,
        email:          values.email || null,
        contacts: values.contacts.map(c => ({
          type:  c.type,
          value: c.type === 'EMAIL' ? c.value.trim().toLowerCase() : onlyDigits(c.value),
        })),
        plan_ids: planIds,
      }
      if (values.password) payload.password = values.password

      if (isEdit && id) await CompaniesService.update(id, payload)
      else              await CompaniesService.create(payload)

      toast.success({
        title: isEdit ? 'Empresa atualizada' : 'Empresa criada',
        description: isEdit ? 'As alterações foram salvas.' : 'A nova empresa foi cadastrada.',
      })
      navigate('/commercial/companies')
    } catch (e) {
      applyApiErrorsToForm(e, form.setError, {
        corporate_name: 'corporateName',
        cpf_cnpj:       'cpfCnpj',
        manager_id:     'managerId',
        postal_code:    'postalCode',
        city_id:        'cityId',
        due_day:        'dueDay',
        payment_method: 'paymentMethod',
      })
    }
  }

  const errs = form.formState.errors
  const basicErr     = !!(errs.name || errs.corporateName || errs.type || errs.cpfCnpj || errs.managerId)
  const locationErr  = !!(errs.postalCode || errs.address || errs.number || errs.district || errs.stateId || errs.cityId)
  const billingErr   = !!(errs.dueDay || errs.paymentMethod)
  const contactsErr  = !!errs.contacts
  const loginErr     = !!(errs.username || errs.password)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </div>
    )
  }

  // ============= 1. Dados =============
  // Sequência pedida: CNPJ → Nome fantasia → Razão social → Tipo → Gestor.
  // Grid de 3 colunas: CNPJ(1) + Nome fantasia(2) | Razão social(3) | Tipo(1) + Gestor(1).
  // Status fica FORA do grid (linha própria abaixo).
  const basicSection = (
    <SectionShell title="Dados" description="Identificação e responsável comercial da empresa.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Controller name="cpfCnpj" control={form.control} render={({ field, fieldState }) => (
          <CpfCnpjField
            personType={personType}
            value={field.value ?? ''}
            onChange={field.onChange}
            error={fieldState.error?.message}
            exceptId={id}
            currentCpfCnpj={cpfCnpj}
            onLookup={async () => {
              const digits = onlyDigits(cpfCnpj)
              if (personType !== 'PESSOA JURÍDICA' || digits.length !== 14) {
                toast.error({ title: 'CNPJ inválido', description: 'Digite um CNPJ com 14 dígitos para buscar.' })
                return
              }
              setCnpjLoading(true)
              try {
                const data = await LookupService.lookupCnpj(digits)
                if (!data) {
                  toast.error({ title: 'CNPJ não encontrado', description: 'Verifique o número digitado.' })
                  return
                }
                // Preenche os campos disponíveis (não sobrescreve quando o usuário
                // já digitou algo — `setValue` só atua se atualmente vazio).
                const get = (k: 'name' | 'corporateName' | 'address' | 'number' | 'complement' | 'district' | 'postalCode') => form.getValues(k) ?? ''
                if (!get('name')          && data.nomeFantasia) form.setValue('name',          data.nomeFantasia, { shouldValidate: true })
                if (!get('corporateName') && data.razaoSocial)  form.setValue('corporateName', data.razaoSocial,  { shouldValidate: true })
                if (!get('address')       && data.address)      form.setValue('address',       data.address,      { shouldValidate: false })
                if (!get('number')        && data.number)       form.setValue('number',        data.number,       { shouldValidate: false })
                if (!get('complement')    && data.complement)   form.setValue('complement',    data.complement,   { shouldValidate: false })
                if (!get('district')      && data.district)     form.setValue('district',      data.district,     { shouldValidate: false })
                if (!get('postalCode')    && data.postalCode)   form.setValue('postalCode',    maskCep(data.postalCode), { shouldValidate: false })
                // Tenta resolver estado e cidade no banco
                if (data.stateAbbr) {
                  const st = states.find(s => s.abbreviation === data.stateAbbr)
                  if (st) {
                    form.setValue('stateId', st.id, { shouldValidate: false })
                    let cs = await LookupService.cities(st.id)
                    // Match ignora acento+caixa: "SAO PAULO" achará "São Paulo"
                    const targetCityNorm = normalizeForMatch(data.cityName)
                    let ci = cs.find(c => normalizeForMatch(c.city) === targetCityNorm)
                    if (!ci && data.cityName) {
                      try {
                        const created = await LookupService.findOrCreateCity(data.cityName, st.abbreviation)
                        ci = created
                        cs = [...cs, created].sort((a, b) => a.city.localeCompare(b.city, 'pt-BR'))
                      } catch { /* ignora */ }
                    }
                    setCities(cs)
                    if (ci) form.setValue('cityId', ci.id, { shouldValidate: false })
                  }
                }
              } catch {
                toast.error({ title: 'Falha ao consultar CNPJ', description: 'Verifique sua conexão e tente novamente.' })
              } finally {
                setCnpjLoading(false)
              }
            }}
            isLoading={cnpjLoading}
          />
        )} />
        <Input
          label="Nome fantasia"
          required
          wrapperClassName="md:col-span-2"
          {...form.register('name')}
          error={errs.name?.message}
        />
        <Input
          label="Razão social"
          required
          wrapperClassName="md:col-span-3"
          {...form.register('corporateName')}
          error={errs.corporateName?.message}
        />
        <Controller name="type" control={form.control} render={({ field, fieldState }) => (
          <Select
            label="Tipo"
            required
            value={field.value}
            onChange={(e) => {
              field.onChange(e.target.value)
              form.setValue('cpfCnpj', '', { shouldValidate: false })
            }}
            options={[
              { value: 'PESSOA FÍSICA',   label: 'Pessoa Física' },
              { value: 'PESSOA JURÍDICA', label: 'Pessoa Jurídica' },
            ]}
            searchable={false} clearable={false}
            error={fieldState.error?.message}
          />
        )} />
        <Controller name="managerId" control={form.control} render={({ field, fieldState }) => (
          <div className="md:col-span-2">
            <GroupedSelect
              label="Gestor responsável"
              required
              value={field.value}
              onChange={field.onChange}
              groups={managerGroups}
              error={fieldState.error?.message}
            />
          </div>
        )} />
      </div>

      <div className="mt-4">
        <Textarea label="Observações" rows={3} {...form.register('observations')}
          placeholder="Anotações internas sobre a empresa" />
      </div>

      {/* Status fora do grid — linha própria abaixo */}
      <Controller name="status" control={form.control} render={({ field }) => (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <StatusToggle value={field.value} onChange={field.onChange} />
        </div>
      )} />
    </SectionShell>
  )

  // ============= 2. Localização =============
  const locationSection = (
    <SectionShell title="Localização" description="Digite o CEP e clique na lupa para preencher automaticamente.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Controller name="postalCode" control={form.control} render={({ field, fieldState }) => (
          <Input
            label="CEP"
            value={field.value ?? ''}
            onChange={(e) => field.onChange(maskCep(e.target.value))}
            placeholder="00000-000"
            error={fieldState.error?.message}
            wrapperClassName="md:col-span-1"
            actionRight={(
              <button
                type="button"
                onClick={lookupCep}
                disabled={cepLoading}
                aria-label="Buscar CEP"
                title="Buscar CEP"
                className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-brand-600 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cepLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              </button>
            )}
          />
        )} />
        <Input label="Logradouro" wrapperClassName="md:col-span-2" {...form.register('address')}    error={errs.address?.message} />
        <Input label="Número"     wrapperClassName="md:col-span-1" {...form.register('number')}     error={errs.number?.message} />
        <Input label="Complemento" wrapperClassName="md:col-span-1" {...form.register('complement')} error={errs.complement?.message} placeholder="Apto, sala" />
        <Input label="Bairro"     wrapperClassName="md:col-span-1" {...form.register('district')}   error={errs.district?.message} />
        <Controller name="stateId" control={form.control} render={({ field, fieldState }) => (
          <Select
            label="Estado"
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value || null)
              form.setValue('cityId', null, { shouldValidate: false })
            }}
            placeholder="UF"
            options={states.map(s => ({ value: s.id, label: s.abbreviation }))}
            error={fieldState.error?.message}
            wrapperClassName="md:col-span-1"
          />
        )} />
        <Controller name="cityId" control={form.control} render={({ field, fieldState }) => (
          <Select
            label="Cidade"
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value || null)}
            placeholder={stateId ? 'Cidade' : 'Selecione o estado primeiro'}
            disabled={!stateId}
            options={cities.map(c => ({ value: c.id, label: c.city }))}
            error={fieldState.error?.message}
            wrapperClassName="md:col-span-2"
          />
        )} />
      </div>
    </SectionShell>
  )

  // ============= 3. Faturamento =============
  const billingSection = (
    <SectionShell title="Faturamento" description="Quando a empresa será cobrada e como.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller name="dueDay" control={form.control} render={({ field, fieldState }) => (
          <Select
            label="Dia de vencimento da fatura"
            required
            value={String(field.value)}
            onChange={(e) => field.onChange(Number(e.target.value))}
            options={[5, 10, 15, 20, 25, 30].map(d => ({ value: String(d), label: `Dia ${d}` }))}
            searchable={false} clearable={false}
            error={fieldState.error?.message}
          />
        )} />
        <Controller name="paymentMethod" control={form.control} render={({ field, fieldState }) => (
          <Select
            label="Método de pagamento"
            required
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            options={[{ value: 'BOLETO', label: 'Boleto' }]}
            searchable={false} clearable={false}
            error={fieldState.error?.message}
          />
        )} />
      </div>
    </SectionShell>
  )

  // ============= 4. Contatos =============
  const contactsSection = (
    <ContactsManager
      value={form.watch('contacts')}
      onChange={(next) => form.setValue('contacts', next, { shouldValidate: form.formState.isSubmitted })}
      errors={errs.contacts as unknown as Array<Record<string, { message?: string }>> | undefined}
      forceShowErrors={form.formState.isSubmitted}
    />
  )

  // ============= 5. Planos =============
  const plansSection = (
    <PlansSelector
      modalities={modalities}
      plans={plans}
      value={form.watch('planSelections')}
      onChange={(next) => form.setValue('planSelections', next, { shouldValidate: true })}
    />
  )

  // ============= 6. Login =============
  const loginSection = (
    <LoginSection
      form={form}
      isEdit={isEdit}
      currentUsername={username}
      exceptId={id}
      errors={{ username: errs.username?.message, email: errs.email?.message, password: errs.password?.message }}
    />
  )

  const sections: FormSection[] = [
    { key: 'basic',    label: 'Dados',         icon: Building2,  content: basicSection,    hasError: basicErr,    errorCount: basicErr ? 1 : 0 },
    { key: 'location', label: 'Localização',   icon: MapPin,     content: locationSection, hasError: locationErr, errorCount: locationErr ? 1 : 0 },
    { key: 'billing',  label: 'Faturamento',   icon: CreditCard, content: billingSection,  hasError: billingErr,  errorCount: billingErr ? 1 : 0 },
    { key: 'contacts', label: 'Contatos',      icon: Phone,      content: contactsSection, hasError: contactsErr, errorCount: contactsErr ? 1 : 0 },
    { key: 'plans',    label: 'Planos',        icon: Package,    content: plansSection },
    { key: 'login',    label: 'Login',         icon: KeyRound,   content: loginSection,    hasError: loginErr,    errorCount: loginErr ? 1 : 0 },
    { key: 'documents', label: 'Documentos',   icon: FileText,   content: (
      <SectionShell title="Documentos" description="Anexe documentos diversos da empresa.">
        <CompanyDocumentsSection companyId={id ?? null} />
      </SectionShell>
    ) },
    { key: 'info',     label: 'Informações',   icon: Network,    content: (
      <SectionShell title="Informações" description="Responsável direto e árvore de comissionamento.">
        <CommissionInfoPanel enabled={isEdit} load={() => CompaniesService.commissionInfo(id!)} />
      </SectionShell>
    ) },
  ]

  return (
    <form
      noValidate
      onSubmit={form.handleSubmit(onSubmit, (e) => {
        if (basicErr)         setActiveSection('basic')
        else if (locationErr) setActiveSection('location')
        else if (billingErr)  setActiveSection('billing')
        else if (contactsErr) setActiveSection('contacts')
        else if (loginErr)    setActiveSection('login')
        onValidationFail(e)
      })}
    >
      <PageHeader
        title={isEdit ? 'Editar empresa' : 'Nova empresa'}
        subtitle="Empresas clientes (PJ ou PF) com planos coletivos"
        crumbs={[{ label: 'Comercial' }, { label: 'Empresas', to: '/commercial/companies' }, { label: isEdit ? 'Editar' : 'Novo' }]}
        backTo="/commercial/companies"
        actions={<Button type="submit" loading={form.formState.isSubmitting} disabled={!canSave}
          title={canSave ? undefined : `Sem permissão para ${isEdit ? 'editar' : 'criar'} empresas`}
          iconLeft={<Save size={14} />}>Salvar</Button>}
      />
      <FormSidemenu sections={sections} activeKey={activeSection} onChange={setActiveSection} />

      <ConfirmDialog
        open={!!dupConfirm}
        onClose={() => setDupConfirm(null)}
        onConfirm={() => { const v = dupConfirm; setDupConfirm(null); if (v) doSave(v) }}
        title="Documento já cadastrado"
        description="Já existe uma empresa com este CPF/CNPJ. Deseja cadastrar mesmo assim?"
        confirmLabel="Cadastrar mesmo assim"
      />
    </form>
  )
}

// ============================================================================
// Subcomponentes
// ============================================================================

function SectionShell({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}

// ----- CPF/CNPJ com check assíncrono de unicidade -----

function CpfCnpjField({
  personType, value, onChange, error, exceptId, currentCpfCnpj, onLookup, isLoading,
}: {
  personType: FormValues['type']
  value: string
  onChange: (v: string) => void
  error?: string
  exceptId?: string
  currentCpfCnpj: string
  /** Disparado pelo clique na lupa (busca CNPJ na BrasilAPI). Só relevante para PJ. */
  onLookup?: () => void | Promise<void>
  /** Estado de loading da busca de CNPJ — controla o spinner no botão da lupa. */
  isLoading?: boolean
}) {
  type CheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'error'
  const [status, setStatus] = useState<CheckStatus>('idle')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const d = onlyDigits(currentCpfCnpj)
    const needLen = personType === 'PESSOA FÍSICA' ? 11 : 14
    if (d.length !== needLen) { setStatus('idle'); return }

    // Só faz o check de unicidade se o número for válido algoritmicamente
    const isValidDoc = personType === 'PESSOA FÍSICA' ? isValidCpf(d) : isValidCnpj(d)
    if (!isValidDoc) { setStatus('idle'); return }

    setStatus('checking')
    debounceRef.current = setTimeout(async () => {
      try {
        const ok = await CompaniesService.checkCpfCnpj(d, exceptId)
        setStatus(ok ? 'available' : 'taken')
      } catch {
        setStatus('error')
      }
    }, 400)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [currentCpfCnpj, personType, exceptId])

  let hint: string | undefined
  let displayError = error
  if (!displayError) {
    if (status === 'checking') hint = 'Verificando'
    // Documento duplicado é PERMITIDO — apenas avisa (confirma em modal ao salvar).
    else if (status === 'taken') hint = `Já existe empresa com este ${personType === 'PESSOA FÍSICA' ? 'CPF' : 'CNPJ'} — você poderá confirmar ao salvar.`
    else if (status === 'error') hint = 'Não foi possível verificar agora — será revalidado ao salvar.'
  }

  // Lupa de busca aparece só para CNPJ (PJ) e quando o callback foi fornecido.
  // X de limpar coexiste (regra global) — o Input cuida disso automaticamente.
  const showLookup = personType === 'PESSOA JURÍDICA' && !!onLookup

  return (
    <Input
      label={personType === 'PESSOA FÍSICA' ? 'CPF' : 'CNPJ'}
      required
      value={value}
      onChange={(e) => onChange(
        personType === 'PESSOA FÍSICA' ? maskCpf(e.target.value) : maskCnpj(e.target.value),
      )}
      placeholder={personType === 'PESSOA FÍSICA' ? '000.000.000-00' : '00.000.000/0000-00'}
      error={displayError}
      hint={hint}
      actionRight={showLookup ? (
        <button
          type="button"
          onClick={() => onLookup?.()}
          disabled={isLoading}
          aria-label="Buscar dados do CNPJ"
          title="Buscar dados do CNPJ"
          className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-brand-600 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
        </button>
      ) : undefined}
    />
  )
}

// ----- GroupedSelect (gestor) -----

function GroupedSelect({
  label, required, value, onChange, groups, wrapperClassName, error,
}: {
  label: string
  required?: boolean
  value: string
  onChange: (v: string) => void
  groups: ManagerGroup[]
  wrapperClassName?: string
  error?: string
}) {
  const options = useMemo(() => {
    const out: Array<{ value: string; label: string }> = []
    for (const g of groups) {
      if (g.users.length === 0) continue
      out.push({ value: `__header__${g.type}`, label: `── ${g.type} ──` })
      for (const u of g.users) out.push({ value: u.id, label: `    ${u.name}` })
    }
    return out
  }, [groups])

  return (
    <Select
      label={label}
      required={required}
      value={value}
      onChange={(e) => {
        const v = e.target.value
        if (v.startsWith('__header__')) return
        onChange(v)
      }}
      placeholder="Buscar gestor"
      options={options}
      wrapperClassName={wrapperClassName}
      error={error}
    />
  )
}

// ----- ContactsManager (sem rótulo, sem validação HTML, ConfirmDialog, layout limpo) -----

const CONTACT_TYPES: { value: CompanyContactType; label: string }[] = [
  { value: 'EMAIL',    label: 'E-mail' },
  { value: 'TELEFONE', label: 'Telefone' },
  { value: 'CELULAR',  label: 'Celular' },
  { value: 'WHATSAPP', label: 'Celular/WhatsApp' },
]

function ContactsManager({
  value, onChange, errors, forceShowErrors,
}: {
  value: CompanyContact[]
  onChange: (next: CompanyContact[]) => void
  errors?: Array<Record<string, { message?: string }>>
  /**
   * Quando true, mostra erros de todos os itens independente do touched.
   * Usado após o submit do form principal para garantir feedback visual.
   */
  forceShowErrors?: boolean
}) {
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null)
  // Marca quais linhas o usuário já interagiu — só mostra erro do valor após touched.
  const [touched, setTouched] = useState<Set<number>>(new Set())

  function add() {
    onChange([...value, { type: 'EMAIL', label: null, value: '' }])
  }
  function confirmRemove() {
    if (deletingIndex === null) return
    onChange(value.filter((_, idx) => idx !== deletingIndex))
    setDeletingIndex(null)
    setTouched(prev => {
      const next = new Set<number>()
      prev.forEach(i => { if (i !== deletingIndex) next.add(i > deletingIndex! ? i - 1 : i) })
      return next
    })
  }
  function update(i: number, patch: Partial<CompanyContact>) {
    onChange(value.map((c, idx) => idx === i ? { ...c, ...patch } : c))
  }
  function maskedFor(type: CompanyContactType, raw: string) {
    return type === 'EMAIL' ? raw : maskPhone(raw)
  }
  function markTouched(i: number) {
    setTouched(prev => {
      if (prev.has(i)) return prev
      const next = new Set(prev); next.add(i); return next
    })
  }

  return (
    <SectionShell title="Contatos" description="Adicione um ou mais canais de contato (e-mail, telefone, celular, WhatsApp).">
      {value.length === 0 ? (
        <p className="text-[13px] text-gray-500 mb-3">Nenhum contato adicionado.</p>
      ) : (
        <ul className="space-y-2 mb-3">
          {value.map((c, i) => {
            const showErr = forceShowErrors || touched.has(i)
            const itemErr = showErr ? errors?.[i] : undefined
            const placeholder = c.type === 'EMAIL' ? 'contato@empresa.com.br' : '(11) 99999-9999'
            return (
              <li key={i} className="bg-gray-50 ring-1 ring-gray-200 rounded-md p-3">
                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] md:items-end gap-3">
                  <Select
                    label="Tipo"
                    required
                    value={c.type}
                    onChange={(e) => update(i, { type: e.target.value as CompanyContactType, value: '' })}
                    options={CONTACT_TYPES}
                    searchable={false} clearable={false}
                  />
                  <Input
                    label="Contato"
                    required
                    value={c.value}
                    onChange={(e) => update(i, { value: maskedFor(c.type, e.target.value) })}
                    onBlur={() => markTouched(i)}
                    placeholder={placeholder}
                    error={itemErr?.value?.message}
                  />
                  <button
                    type="button"
                    onClick={() => setDeletingIndex(i)}
                    aria-label="Remover contato"
                    className="cursor-pointer inline-flex items-center justify-center w-9 h-9 rounded-md text-rose-500 hover:bg-rose-50 transition self-end mb-[2px]"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
      <Button type="button" size="sm" variant="ghost" onClick={add} iconLeft={<Plus size={12} />}>
        Adicionar contato
      </Button>

      <ConfirmDialog
        open={deletingIndex !== null}
        onClose={() => setDeletingIndex(null)}
        onConfirm={confirmRemove}
        title="Remover contato?"
        description="Tem certeza que deseja remover este contato? A ação só será aplicada quando você salvar a empresa."
        confirmLabel="Remover"
        variant="danger"
      />
    </SectionShell>
  )
}

// ----- PlansSelector -----

function PlansSelector({
  modalities, plans, value, onChange,
}: {
  modalities: Modality[]
  plans: Plan[]
  value: Record<string, string | null>
  onChange: (next: Record<string, string | null>) => void
}) {
  function setForModality(modalityId: string, planId: string | null) {
    onChange({ ...value, [modalityId]: planId })
  }

  return (
    <SectionShell
      title="Planos comercializáveis"
      description="Selecione um plano por modalidade. Não é obrigatório preencher todas — modalidades sem seleção ficam de fora."
    >
      {modalities.length === 0 ? (
        <p className="text-[13px] text-gray-500 italic">
          Nenhuma modalidade ativa cadastrada. Crie modalidades em Configurações → Comercial → Modalidades.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {modalities.map(m => {
            const opts = plans.filter(p => p.planTypeRelationshipId === m.id)
            return (
              <Select
                key={m.id}
                label={m.name + (m.minAge != null ? ` (a partir de ${m.minAge} anos)` : '')}
                value={value[m.id] ?? ''}
                onChange={(e) => setForModality(m.id, e.target.value || null)}
                placeholder={opts.length === 0 ? 'Sem planos para esta modalidade' : 'Selecione um plano'}
                disabled={opts.length === 0}
                options={opts.map(p => ({
                  value: p.id,
                  label: `${p.name} — ${p.formatted?.price ?? `R$ ${p.price.toFixed(2).replace('.', ',')}`}`,
                }))}
              />
            )
          })}
        </div>
      )}
    </SectionShell>
  )
}

// ----- LoginSection -----

type LoginFormShape = {
  watch: (n: 'username') => string
  register: (n: 'username' | 'email' | 'password') => Record<string, unknown>
  setValue: (n: 'username' | 'password', v: string, opts?: { shouldValidate?: boolean }) => void
}

function LoginSection({
  form, isEdit, currentUsername, exceptId, errors,
}: {
  form: LoginFormShape
  isEdit: boolean
  currentUsername: string
  exceptId?: string
  errors: { username?: string; email?: string; password?: string }
}) {
  type CheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'error'
  const [status, setStatus] = useState<CheckStatus>('idle')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const initialUsernameRef = useRef<string>(currentUsername)
  useEffect(() => { initialUsernameRef.current = currentUsername }, [isEdit])  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const u = currentUsername.trim()
    if (u.length === 0) { setStatus('idle'); return }
    if (isEdit && u === initialUsernameRef.current) { setStatus('available'); return }

    // IMPORTANTE: durante os 2s de debounce o status fica 'idle' (sem hint
    // visual), para não dar impressão de que o backend está sendo consultado
    // a cada keystroke. O 'checking' só aparece quando a requisição realmente
    // dispara, e o resultado vira 'available' | 'taken' | 'error'.
    setStatus('idle')
    debounceRef.current = setTimeout(async () => {
      setStatus('checking')
      try {
        const ok = await CompaniesService.checkUsername(u, exceptId)
        setStatus(ok ? 'available' : 'taken')
      } catch {
        setStatus('error')
      }
    }, 2000)

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [currentUsername, isEdit, exceptId])

  let usernameHint: string | undefined
  let usernameError = errors.username
  if (!usernameError) {
    switch (status) {
      case 'checking':  usernameHint = 'Verificando disponibilidade'; break
      case 'available': usernameHint = 'Usuário disponível.'; break
      case 'taken':     usernameError = 'Este nome de usuário já está em uso.'; break
      case 'error':     usernameHint = 'Não foi possível verificar agora — será revalidado ao salvar.'; break
    }
  }

  return (
    <SectionShell title="Login" description="Credenciais que a empresa usará para acessar o painel.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col">
          <Input
            label="Usuário"
            required
            value={currentUsername}
            onChange={(e) => {
              // Slug em tempo real: lowercase, sem espaço, sem acento.
              // Espaço vira "."; acentos viram letra raiz; chars inválidos descartados.
              form.setValue('username', slugifyUsername(e.target.value), { shouldValidate: true })
            }}
            error={usernameError}
            hint={usernameHint}
            placeholder="login.empresa"
          />
          {!usernameError && status === 'available' && currentUsername.length > 0 && (
            <span className="text-[11px] text-emerald-600 mt-1 inline-flex items-center gap-1">
              <CheckCircle2 size={11} /> Usuário disponível
            </span>
          )}
          {!usernameError && status === 'checking' && (
            <span className="text-[11px] text-gray-500 mt-1 inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Verificando
            </span>
          )}
          {status === 'taken' && (
            <span className="text-[11px] text-rose-600 mt-1 inline-flex items-center gap-1">
              <AlertCircle size={11} /> Já está em uso
            </span>
          )}
        </div>
        <Input
          label="E-mail de acesso"
          type="email"
          {...form.register('email')}
          error={errors.email}
          hint="E-mail do usuário da empresa (login/recuperação)."
          placeholder="empresa@email.com"
        />
        <Input
          label={isEdit ? 'Nova senha' : 'Senha'}
          required={!isEdit}
          type="password"
          {...form.register('password')}
          error={errors.password}
          hint="Mínimo 8 caracteres."
        />
      </div>
    </SectionShell>
  )
}
